import { pipe } from "fp";

import * as Data from "~/Data";
import * as Driver from "~/Driver";
import * as Messaging from "~/Messaging";
import { Mutable } from "~/Mutable";
import { useSelector, State as GlobalState } from "~/State";

/**
 * `Active` represents the `Driver` which is associated with the collection of messages currently being viewed.
 */
export type Active = Driver.Driver | undefined;

export type When<A> = {
  readonly inactive: () => A;
  readonly unavailable?: (active: Driver.Driver) => A;

  // eslint-disable-next-line functional/prefer-readonly-type
  readonly available: (active: Mutable<Messaging.Message>[]) => A;
};

export const equals = (a: Active) => (b: Active): boolean => {
  if (a === undefined && b === undefined) return true;
  if (a === undefined || b === undefined) return false;
  return Driver.equals(a, b);
};

export const isActive = (b: Active) => pipe(use(), equals(b));

export const use = (): Active =>
  useSelector({ equals, select: ({ messages: { active } }) => active });

export const useActiveInitials = (): string =>
  pipe(
    {
      active: use(),
    },
    ({ active }) =>
      active && active.name
        ? `${active.name.first?.slice(0, 1)}${active.name.last?.slice(0, 1)}`
        : ""
  );

export const useWhen = <A>({
  inactive,
  unavailable = inactive,
  available,
}: When<A>): A =>
  pipe(
    {
      active: use(),
      store: Messaging.useStore(),
    },

    ({ active, store }) =>
      !active
        ? inactive()
        : pipe(
            store,
            Data.Store.Get.fromReference({
              id: active.id,
              missing: () => unavailable(active),
              found: (messages) =>
                available(messages as Mutable<readonly Messaging.Message[]>),
            })
          )
  );

export namespace State {
  export type State = Active;

  export const initial = (): State => undefined;

  export const when = <A>(
    { messages: { active, store } }: Mutable<GlobalState>,
    { inactive, unavailable = inactive, available }: When<A>
  ): A =>
    !active
      ? inactive()
      : pipe(
          store,
          Data.Store.Get.fromReference({
            id: active.id,
            missing: () => unavailable(active),
            found: (messages) => available(messages),
          })
        );
}
